










































































































































































































































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";

import { endpoints } from "@/endpoints";
import { eventLoading } from "@/components/sce/EventLoading";
import { fmtFloat, fmtKm } from "./formatters";
import { helpForControl } from "./catalogHelpers";
import { buildRcaUrl, RcaFilters } from "./filterQueryParams";
import HelpIcon from "./HelpIcon.vue";
import SelectedExposurePanel from "./SelectedExposurePanel.vue";

const mapboxgl = require("mapbox-gl");

const MARKERS_SOURCE_ID = "rca-map-markers";
const LINES_SOURCE_ID = "rca-map-lines";
const MARKERS_LAYER_ID = "rca-map-markers-layer";
const LINES_LAYER_ID = "rca-map-lines-layer";

/** Marker colour ramp for log10(|trailer delta| + 1) — kept in sync with the .color-legend-bar
 * gradient in the stylesheet. Stops at 0, 0.35, 0.7, 1 of the [cmin, cmax] range. */
const MARKER_RAMP = ["#1d3557", "#277da1", "#f4a261", "#d62828"];

@Component({ components: { SelectedExposurePanel, HelpIcon } })
export default class MapTab extends Vue {
    @Prop({ required: true }) startDate!: string;
    @Prop({ required: true }) endDate!: string;
    @Prop({ required: true }) mapDate!: string;
    /** Metric catalog payload — supplies tooltip help text. */
    @Prop({ default: null }) catalog!: any;
    /** Whether this tab is the active one — mapbox-gl mounts at 0×0 when hidden, so we
     * resize whenever this becomes true. */
    @Prop({ default: true }) active!: boolean;
    /** Global sidebar filters — narrow the map links to matching trips. */
    @Prop({ default: () => ({}) }) filters!: RcaFilters;
    /** All available scenario dates — drives the badge date picker's allowed-dates. */
    @Prop({ default: () => [] }) availableDates!: string[];

    /** True while the in-map date picker menu is open. */
    datePickerOpen = false;

    fmtFloat = fmtFloat;

    mapboxAccessToken =
        "pk.eyJ1IjoiYm9uMTMyIiwiYSI6ImNqdXRhYmw1OTA1eGUzeW5yZGo3OWZmankifQ.RYbaSeGdz3Nq_hIGWXrpSw";
    mapboxStyleUrl = "mapbox://styles/bon132/cmm8gavux000101sjauuwfvpe";

    rows: any[] = [];
    loading = false;
    error: string | null = null;

    selectedStates: string[] = [];
    showLinkLines: boolean = true;
    /**
     * Slider model — minimum log10(|trailer delta| + 1) a link must reach to be drawn on the
     * map. Map-only declutter control: it narrows `mapRows` (markers + link lines) but not
     * `filteredRows`, so the Closed-links table and the drill-in panel still show everything.
     */
    minTrailerDeltaLog = 0;
    /**
     * The most recently clicked row from the map or the table drill button. Carries both the
     * link_id and the road_name so the SelectedExposurePanel can apply whichever matches its
     * current selection mode (Link vs Road name).
     */
    clickedRow: { link_id: number; road_name: string } | null = null;

    private map: any = null;
    private popup: any = null;
    private resizeObserver: any = null;
    private mapLoaded = false;
    /** Debounce timer for the watcher-driven refetch — see onParamsChanged. */
    private fetchTimer: number | null = null;

    mounted() {
        this.fetchRows();
        this.initMap();
    }

    beforeDestroy() {
        if (this.fetchTimer != null) {
            window.clearTimeout(this.fetchTimer);
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.popup) {
            this.popup.remove();
            this.popup = null;
        }
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    @Watch("active")
    onActiveChanged(now: boolean) {
        if (!now || !this.map) return;
        // Two-step resize after activation — let the v-tab-item finish its display
        // transition before mapbox re-measures the container.
        requestAnimationFrame(() => {
            window.setTimeout(() => {
                if (this.map) this.map.resize();
            }, 80);
        });
    }

    helpFor(controlId: string): string {
        return helpForControl(this.catalog, controlId);
    }

    /**
     * v-date-picker allowed-dates predicate for the badge picker. Only scenario days within
     * the current start/end range are clickable.
     */
    isAllowedMapDate(val: string): boolean {
        if (this.availableDates.length && !this.availableDates.includes(val)) {
            return false;
        }
        if (this.startDate && val < this.startDate) return false;
        if (this.endDate && val > this.endDate) return false;
        return true;
    }

    /** Emit the new date up to the parent (which owns mapDate) and close the menu. */
    onMapDateSelect(value: string) {
        this.datePickerOpen = false;
        this.$emit("update:map-date", value);
    }

    @Watch("startDate")
    @Watch("endDate")
    @Watch("mapDate")
    @Watch("filters", { deep: true })
    onParamsChanged() {
        // A single date-selector commit changes startDate, endDate and (after clamping)
        // mapDate within a few ticks, firing this 2-3 times. Debounce to one fetch.
        if (this.fetchTimer != null) {
            window.clearTimeout(this.fetchTimer);
        }
        this.fetchTimer = window.setTimeout(() => {
            this.fetchTimer = null;
            this.fetchRows();
        }, 60);
    }

    fetchRows() {
        if (!this.startDate || !this.endDate || !this.mapDate) return;
        this.loading = true;
        this.error = null;
        eventLoading.startLoading();
        Vue.axios({
            url: buildRcaUrl(
                endpoints.roadClosureAnalyticsMapLinksUrl,
                {
                    start_date: this.startDate,
                    end_date: this.endDate,
                    scenario_date: this.mapDate,
                },
                this.filters,
            ),
        })
            .then((response: any) => {
                const rows: any[] = response.data || [];
                this.rows = rows.map((r) => ({
                    ...r,
                    length_km: (Number(r.length_m) || 0) / 1000,
                }));
                // Reset state filter to "all available" so the new date is fully visible.
                this.selectedStates = this.uniqueStates(this.rows);
                // Default selection to the first row so the exposure panel pre-loads.
                this.clickedRow =
                    this.rows.length > 0
                        ? {
                              link_id: this.rows[0].link_id,
                              road_name: this.rows[0].road_name,
                          }
                        : null;
            })
            .catch((err: any) => {
                console.error("[RCA Map]", err);
                this.error = err?.message || "Failed to load.";
                this.rows = [];
            })
            .finally(() => {
                this.loading = false;
                eventLoading.stopLoading();
            });
    }

    splitLabel(value: any): string[] {
        if (value === null || value === undefined) return [];
        return String(value)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
    }

    uniqueStates(rows: any[]): string[] {
        const set = new Set<string>();
        for (const r of rows) {
            for (const s of this.splitLabel(r.state_names_label)) set.add(s);
        }
        return Array.from(set).sort();
    }

    get availableStates(): string[] {
        return this.uniqueStates(this.rows);
    }

    /** log10(|trailer delta| + 1) for a row — the value the marker colour encodes. */
    private logDelta(row: any): number {
        return Math.log10((Number(row.abs_road_trailer_delta) || 0) + 1);
    }

    get deltaSliderMin(): number {
        const vals = this.filteredRows.map((r) => this.logDelta(r));
        return vals.length ? Math.min(...vals) : 0;
    }
    get deltaSliderMax(): number {
        const vals = this.filteredRows.map((r) => this.logDelta(r));
        return vals.length ? Math.max(...vals) : 0;
    }
    get deltaSliderStep(): number {
        const span = this.deltaSliderMax - this.deltaSliderMin;
        return span > 0 ? span / 100 : 0.01;
    }

    /** Convert a log-space slider value back to a human-readable |trailer delta|. */
    formatDelta(logValue: number): string {
        const delta = Math.pow(10, logValue) - 1;
        if (delta < 10) return delta.toFixed(1);
        return Math.round(delta).toLocaleString();
    }

    /** Reset the delta slider to "show all" whenever the displayed row set changes. */
    @Watch("filteredRows")
    onFilteredRowsChanged() {
        this.minTrailerDeltaLog = this.deltaSliderMin;
    }

    get filteredRows(): any[] {
        if (!this.selectedStates.length) return this.rows;
        const wanted = new Set(this.selectedStates);
        return this.rows.filter((r) =>
            this.splitLabel(r.state_names_label).some((s) => wanted.has(s)),
        );
    }

    /**
     * Rows actually plotted on the map: `filteredRows` narrowed by the trailer-delta slider.
     * The Closed-links table and the drill-in panel deliberately keep using `filteredRows`.
     */
    get mapRows(): any[] {
        if (this.minTrailerDeltaLog <= this.deltaSliderMin) {
            return this.filteredRows;
        }
        return this.filteredRows.filter(
            (r) => this.logDelta(r) >= this.minTrailerDeltaLog,
        );
    }

    /** Fixed top-25 slice of `filteredRows` for the Closed-links table. */
    get tableRows(): any[] {
        return this.filteredRows.slice(0, 25);
    }

    // -------------------------------------------------------------------------
    // mapbox-gl map
    // -------------------------------------------------------------------------

    async initMap() {
        await this.$nextTick();
        const container = this.$refs.mapContainer as HTMLElement | undefined;
        if (!container) return;
        try {
            mapboxgl.accessToken = this.mapboxAccessToken;
            this.map = new mapboxgl.Map({
                container,
                style: this.mapboxStyleUrl,
                // Fixed Australia-wide view, matching the Geography tab.
                center: [134, -25],
                zoom: 3.2,
                attributionControl: false,
            });
            // Bottom-right so the zoom control clears the top-right map-date badge.
            this.map.addControl(
                new mapboxgl.NavigationControl(),
                "bottom-right",
            );
            this.map.on("load", () => {
                this.addLayers();
                this.mapLoaded = true;
                this.renderData();
                if (this.map) this.map.resize();
            });
            const ROCtor = (window as any).ResizeObserver;
            if (typeof ROCtor !== "undefined") {
                this.resizeObserver = new ROCtor(() => {
                    if (this.map) this.map.resize();
                });
                this.resizeObserver.observe(container);
            }
        } catch (err) {
            const e = err as any;
            console.error("[RCA Map mapbox-gl]", e);
            this.error = e?.message || "Could not initialise map.";
        }
    }

    /** Add the (initially empty) GeoJSON sources plus the line-overlay and marker layers.
     * The red link-line overlay renders under the coloured markers. */
    addLayers() {
        const empty = { type: "FeatureCollection", features: [] };
        this.map.addSource(LINES_SOURCE_ID, { type: "geojson", data: empty });
        this.map.addSource(MARKERS_SOURCE_ID, { type: "geojson", data: empty });

        this.map.addLayer({
            id: LINES_LAYER_ID,
            type: "line",
            source: LINES_SOURCE_ID,
            layout: {
                "line-cap": "round",
                "line-join": "round",
                visibility: this.showLinkLines ? "visible" : "none",
            },
            paint: {
                "line-color": "rgba(214, 40, 40, 0.85)",
                "line-width": 3,
            },
        });
        this.map.addLayer({
            id: MARKERS_LAYER_ID,
            type: "circle",
            source: MARKERS_SOURCE_ID,
            paint: {
                // circle-radius reads a per-feature value; circle-color is re-set per
                // render once the colour range is known.
                "circle-radius": ["get", "radius"],
                "circle-color": MARKER_RAMP[3],
                "circle-opacity": 0.88,
            },
        });

        this.attachHandlers();
    }

    private markerDiameter(km: number): number {
        const sized = 4.5 + Math.sqrt(Math.max(km, 0)) * 1.25;
        return Math.max(5, Math.min(15, sized));
    }

    /** circle-color interpolate expression over log10(|trailer delta| + 1). Pinned to the
     * full state-filtered range so a given delta keeps its colour as the slider moves. */
    private colorExpression(cmin: number, cmax: number): any {
        const span = cmax - cmin;
        if (span <= 0) return MARKER_RAMP[3];
        return [
            "interpolate",
            ["linear"],
            ["get", "wlog"],
            cmin,
            MARKER_RAMP[0],
            cmin + span * 0.35,
            MARKER_RAMP[1],
            cmin + span * 0.7,
            MARKER_RAMP[2],
            cmax,
            MARKER_RAMP[3],
        ];
    }

    /** Push the current rows into the map: coloured markers and the closed-link line
     * overlay. Re-colours the markers to the current delta range. */
    renderData() {
        if (!this.map || !this.mapLoaded) return;

        const scaleValues = this.filteredRows.map((r) => this.logDelta(r));
        const cmax = scaleValues.length ? Math.max(1, ...scaleValues) : 1;
        const cmin = scaleValues.length ? Math.min(...scaleValues) : 0;

        const markerFeatures: any[] = [];
        const lineFeatures: any[] = [];
        for (const r of this.mapRows) {
            if (r.lon != null && r.lat != null) {
                markerFeatures.push({
                    type: "Feature",
                    geometry: { type: "Point", coordinates: [r.lon, r.lat] },
                    properties: {
                        link_id: r.link_id,
                        road_name: r.road_name || "(unnamed road)",
                        wlog: this.logDelta(r),
                        radius: this.markerDiameter(r.length_km || 0) / 2,
                        length_km: r.length_km || 0,
                        abs_road_trailer_delta:
                            Number(r.abs_road_trailer_delta) || 0,
                        road_trailer_delta:
                            r.road_trailer_delta == null
                                ? null
                                : Number(r.road_trailer_delta),
                        closure_event_count: r.closure_event_count || 0,
                        closure_category_names_label:
                            r.closure_category_names_label || "",
                        consecutive_days_closed: r.consecutive_days_closed || 0,
                        state_names_label: r.state_names_label || "",
                        lga_names_label: r.lga_names_label || "",
                    },
                });
            }
            if (this.showLinkLines && r.geometry_geojson) {
                let geom: any = null;
                try {
                    geom = JSON.parse(r.geometry_geojson);
                } catch (e) {
                    geom = null;
                }
                if (
                    geom &&
                    (geom.type === "LineString" ||
                        geom.type === "MultiLineString")
                ) {
                    lineFeatures.push({
                        type: "Feature",
                        geometry: geom,
                        properties: {},
                    });
                }
            }
        }

        (this.map.getSource(MARKERS_SOURCE_ID) as any).setData({
            type: "FeatureCollection",
            features: markerFeatures,
        });
        (this.map.getSource(LINES_SOURCE_ID) as any).setData({
            type: "FeatureCollection",
            features: lineFeatures,
        });
        this.map.setPaintProperty(
            MARKERS_LAYER_ID,
            "circle-color",
            this.colorExpression(cmin, cmax),
        );
    }

    @Watch("mapRows")
    onMapRowsChanged() {
        this.renderData();
    }

    /** The line overlay toggle just flips layer visibility (the line features are always
     * kept in the source by renderData, which the toggle also re-runs). */
    @Watch("showLinkLines")
    onShowLinkLinesChanged() {
        if (!this.map || !this.mapLoaded) return;
        this.map.setLayoutProperty(
            LINES_LAYER_ID,
            "visibility",
            this.showLinkLines ? "visible" : "none",
        );
        this.renderData();
    }

    attachHandlers() {
        this.popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 10,
        });
        this.map.on("mousemove", MARKERS_LAYER_ID, (e: any) => {
            const feature = e.features && e.features[0];
            if (!feature) return;
            this.map.getCanvas().style.cursor = "pointer";
            this.popup
                .setLngLat(feature.geometry.coordinates)
                .setHTML(this.markerPopupHtml(feature.properties))
                .addTo(this.map);
        });
        this.map.on("mouseleave", MARKERS_LAYER_ID, () => {
            this.map.getCanvas().style.cursor = "";
            if (this.popup) this.popup.remove();
        });
        this.map.on("click", MARKERS_LAYER_ID, (e: any) => {
            const feature = e.features && e.features[0];
            if (!feature) return;
            const p = feature.properties;
            this.setSelection(Number(p.link_id), String(p.road_name || ""));
        });
    }

    private markerPopupHtml(p: any): string {
        const delta =
            p.road_trailer_delta == null
                ? "NA"
                : fmtFloat(p.road_trailer_delta);
        const abs = Number(p.abs_road_trailer_delta) || 0;
        return (
            `<div class="rca-map-popup">` +
            `<div class="popup-name">${p.road_name || "(unnamed road)"}</div>` +
            `<div><span class="popup-k">Length:</span> ${fmtKm(
                p.length_km,
            )}</div>` +
            `<div><span class="popup-k">Change in trailers vs baseline (absolute):</span> ${fmtFloat(
                abs,
            )}</div>` +
            `<div><span class="popup-k">Change in trailers vs baseline:</span> ${delta}</div>` +
            `<div><span class="popup-k">Closure events:</span> ${
                p.closure_event_count || 0
            }</div>` +
            `<div><span class="popup-k">Closure categories:</span> ${
                p.closure_category_names_label || ""
            }</div>` +
            `<div><span class="popup-k">Consecutive days closed:</span> ${
                p.consecutive_days_closed || 0
            }</div>` +
            `<div><span class="popup-k">States:</span> ${
                p.state_names_label || ""
            }</div>` +
            `<div><span class="popup-k">LGAs:</span> ${
                p.lga_names_label || ""
            }</div>` +
            `</div>`
        );
    }

    setSelection(linkId: number, roadName: string) {
        // Always reassign with a fresh object so the panel's @Watch fires on every click.
        this.clickedRow = { link_id: linkId, road_name: roadName };
    }

    /**
     * Reference circles for the size legend. The marker size formula
     * `4.5 + sqrt(km) * 1.25` is clamped to [5, 15] px; we sample three pixel sizes from
     * the drawn markers and back-compute the length each represents (the saturated upper
     * bound gets a `≥` prefix).
     */
    get sizeLegendStops(): { label: string; px: number }[] {
        const lengths = this.filteredRows
            .map((r) => Number(r.length_km))
            .filter((v) => Number.isFinite(v) && v >= 0);
        if (!lengths.length) return [];

        const PX_MIN = 5;
        const PX_MAX = 15;
        const lengthFor = (px: number) => {
            const t = (px - 4.5) / 1.25;
            return t > 0 ? t * t : 0;
        };
        const SATURATION_KM = lengthFor(PX_MAX);
        const formatKm = (km: number, prefix = "") => {
            if (km < 1) return `${prefix}${km.toFixed(2)} km`;
            if (km < 10) return `${prefix}${km.toFixed(1)} km`;
            return `${prefix}${Math.round(km)} km`;
        };
        const labelFor = (px: number) => {
            if (px >= PX_MAX - 1e-6) return formatKm(SATURATION_KM, "≥");
            return formatKm(lengthFor(px));
        };

        const sizes = lengths
            .map((km) => this.markerDiameter(km))
            .sort((a, b) => a - b);
        const minPx = sizes[0];
        const maxPx = sizes[sizes.length - 1];
        const midPx = (minPx + maxPx) / 2;

        const stops = [
            { px: minPx, label: labelFor(minPx) },
            { px: midPx, label: labelFor(midPx) },
            { px: maxPx, label: labelFor(maxPx) },
        ];
        const seen = new Set<string>();
        return stops.filter((s) => {
            if (seen.has(s.label)) return false;
            seen.add(s.label);
            return true;
        });
    }

    tableHeaders = [
        { text: "", value: "actions", sortable: false, width: 36 },
        { text: "Road segment ID", value: "link_id" },
        { text: "Road", value: "road_name" },
        { text: "Length (km)", value: "length_km", align: "end" },
        {
            text: "Consecutive days closed",
            value: "consecutive_days_closed",
            align: "end",
        },
        {
            text: "Closure events",
            value: "closure_event_count",
            align: "end",
        },
        {
            text: "Change in trailers vs baseline (absolute)",
            value: "abs_road_trailer_delta",
            align: "end",
        },
        { text: "Categories", value: "closure_category_names_label" },
        { text: "States", value: "state_names_label" },
        { text: "LGAs", value: "lga_names_label" },
    ];
}
